import { apiService } from "./api.service";
import { Project, ProjectData } from "../types";
import { reportService } from "./report.service";

class ProjectService {
  private static instance: ProjectService;

  private constructor() {}

  static getInstance(): ProjectService {
    if (!ProjectService.instance) {
      ProjectService.instance = new ProjectService();
    }
    return ProjectService.instance;
  }

  async saveProject(project: Project, userId: string): Promise<Project> {

    // Prepare project data for API
    const projectData = {
      name: project.name,
      description: project.description,
      nodes: project.nodes || [],
      edges: Array.isArray(project.edges) ? project.edges : [],
      owner_id: project.owner_id,
      shared_with: project.shared_with || [],
      is_locked: project.is_locked || false,
      is_archived: project.is_archived || false,
      version: project.version || 1,
      settings: project.settings || {}
    };

    if (project.id === 'unsaved-project') {
      projectData.owner_id = userId;
      const data = await apiService.createProject(projectData);
      return new Project(data);
    } else {
      const data = await apiService.updateProject(project.id, projectData);
      return new Project(data);
    }
  }

  async loadProject(projectId: string, isPreview?: boolean): Promise<Project> {
    
    try {
      // Get the preview ID from the URL if in preview mode
      const urlParams = new URLSearchParams(window.location.search);
      const urlPreviewId = urlParams.get('preview');
      
      // If we're in preview mode and there's a preview token in the URL, use it
      // Otherwise, try to load the project normally - it might have public access
      // or the user might be authenticated and have access rights
      if (isPreview && !urlPreviewId) {
      }
      
      const data = await apiService.getProject(projectId);
      
      // If we got data back, we're good to go
      if (!data) {
        console.error('ProjectService: No data returned from API');
        throw new Error('Failed to load project data');
      }
      
      // Ensure edges are properly initialized
      const projectData = {
        ...data,
        edges: Array.isArray(data.edges) ? data.edges : []
      };
      
      const project = new Project(projectData);
      
      return project;
    } catch (error) {
      console.error('ProjectService: Failed to load project:', error);
      throw error;
    }
  }

  async getUserProjects(showAll: boolean = false): Promise<Project[]> {
    const MAX_RETRIES = 5;
    let retries = 0;
    let delay = 1000; // Start with 1 second delay

    while (retries < MAX_RETRIES) {
      try {
        // If showAll is true, add a query parameter to the request
        // The backend already has logic to return all projects for admin users
        const projects = await apiService.getProjects(showAll);
        if (!projects) {
          return [];
        }
        return Array.isArray(projects) ? projects.map(project => new Project(project)) : [];
      } catch (error: any) {
        console.error(`Attempt ${retries + 1} failed to load user projects:`, error);

        // Only retry if it's an authentication token error
        if (error.message === 'No authentication token available' || error.message.includes('401')) {
          retries++;
          if (retries < MAX_RETRIES) {
            console.log(`Retrying in ${delay / 1000} seconds...`);
            await new Promise(resolve => setTimeout(resolve, delay));
            delay *= 2; // Exponential backoff
          } else {
            console.error('Max retries reached. Failed to load user projects.');
            throw error; // Re-throw after max retries
          }
        } else {
          // For other errors, don't retry, just re-throw
          throw error;
        }
      }
    }
    return []; // Should not be reached
  }

  async deleteProject(projectId: string): Promise<void> {
    // Clear report settings from localStorage when deleting a project
    reportService.clearReportFromLocalStorage();
    
    await apiService.deleteProject(projectId);
  }

  // New methods for collaborator management
  async addCollaborator(projectId: string, userId: string, role: 'editor' | 'viewer'): Promise<void | { message: string }> {
    return apiService.addCollaborator(projectId, { userId, role });
  }

  async removeCollaborator(projectId: string, userId: string): Promise<void> {
    await apiService.removeCollaborator(projectId, userId);
  }

  // New methods for project locking
  async lockProject(projectId: string): Promise<Project> {
    const data = await apiService.lockProject(projectId);
    return new Project(data);
  }

  async unlockProject(projectId: string): Promise<Project> {
    const data = await apiService.unlockProject(projectId);
    return new Project(data);
  }
  
  // New methods for public access
  async getProjectPublicAccess(projectId: string): Promise<'No Access' | 'Can View' | 'Can Edit'> {
    const data = await apiService.getProjectPublicAccess(projectId);
    return data.accessLevel;
  }

  async setProjectPublicAccess(projectId: string, accessLevel: 'No Access' | 'Can View' | 'Can Edit'): Promise<Project> {
    const data = await apiService.setProjectPublicAccess(projectId, accessLevel);
    return new Project(data);
  }

  // New methods for import/export
  async exportProject(projectId: string): Promise<ProjectData> {
    return apiService.exportProject(projectId);
  }

  async importProject(projectData: {
    name: string;
    description?: string;
    nodes: any[];
    edges: any[];
  }): Promise<Project> {
    const data = await apiService.importProject(projectData);
    return new Project(data);
  }
}

export const projectService = ProjectService.getInstance();
